import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { Card, Btn, Empty, Table, Td, Badge } from "../components/UI";
import { FLabel, FInput, CheckBox } from "./engagement/FormControls";
import { SlidePanel } from "../components/SlidePanel";
import { useClients, ClientSelect } from "./clinical/shared";
import { Send, Copy, Mail, BarChart3, Bell, Trash2, ClipboardList, PenLine, Users, Building2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ICONS = { client_satisfaction: ClipboardList, client_entrance: PenLine, client_exit: PenLine, stakeholder_satisfaction: Building2, personnel_satisfaction: Users, corporate_compliance: ShieldCheck };
const surveyLink = (token) => `${window.location.origin}/survey/${token}`;

function InviteDialog({ tpl, onClose, onSent }) {
  const clients = useClients();
  const [form, setForm] = useState({ recipient_name: "", recipient_email: "", client_id: "", anonymous: tpl.key === "personnel_satisfaction" || tpl.key === "corporate_compliance" });
  const [invite, setInvite] = useState(null);
  const send = async () => {
    try {
      const r = await api.post("/surveys/invites", { survey_key: tpl.key, ...form });
      setInvite(r.data); onSent();
      toast.success("Invite created - share the link below");
    } catch (e) { toast.error(apiError(e)); }
  };
  const copy = () => { navigator.clipboard.writeText(surveyLink(invite.token)); toast.success("Link copied to clipboard"); };
  const pickClient = (id) => {
    const c = clients.find((x) => x.id === id);
    setForm((s) => ({ ...s, client_id: id, recipient_name: c ? `${c.first_name} ${c.last_name}` : s.recipient_name }));
  };
  return (
    <SlidePanel open onClose={onClose} title={`Send Invite - ${tpl.name}`} subtitle={tpl.description} testId="survey-invite-panel">
      {!invite ? (
        <div className="space-y-4">
          {tpl.audience === "client" && (
            <div><FLabel>Client</FLabel><ClientSelect clients={clients} value={form.client_id} onChange={pickClient} testId="survey-invite-client" /></div>
          )}
          <div><FLabel>Recipient name</FLabel><FInput data-testid="survey-invite-name" value={form.recipient_name} onChange={(e) => setForm((s) => ({ ...s, recipient_name: e.target.value }))} placeholder={tpl.audience === "personnel" ? "Staff member (optional if anonymous)" : "Full name"} /></div>
          <div><FLabel>Recipient email (optional)</FLabel><FInput data-testid="survey-invite-email" type="email" value={form.recipient_email} onChange={(e) => setForm((s) => ({ ...s, recipient_email: e.target.value }))} placeholder="name@example.com" /></div>
          <CheckBox testId="survey-invite-anonymous" checked={form.anonymous} onChange={() => setForm((s) => ({ ...s, anonymous: !s.anonymous }))} label="Anonymous response (recommended for staff surveys)" />
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
            <Btn onClick={send} data-testid="survey-invite-send-btn"><Send size={14} /> Create Invite Link</Btn>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="survey-invite-created">
          <div className="rounded-xl bg-[#E8F5E9] p-4 text-sm font-semibold text-[#2E7D32]">✓ Invite created for {invite.recipient_name || "anonymous respondent"}</div>
          <div><FLabel>Shareable survey link</FLabel>
            <div className="flex gap-2">
              <FInput data-testid="survey-invite-link" readOnly value={surveyLink(invite.token)} className="flex-1 text-xs" />
              <Btn variant="outline" onClick={copy} data-testid="survey-invite-copy-btn"><Copy size={14} /></Btn>
            </div>
          </div>
          {invite.recipient_email && (
            <a data-testid="survey-invite-mailto" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#4E342E] hover:underline"
              href={`mailto:${invite.recipient_email}?subject=${encodeURIComponent(`Please complete: ${tpl.name}`)}&body=${encodeURIComponent(`Hello ${invite.recipient_name},\n\nPlease take a few minutes to complete our ${tpl.name}:\n${surveyLink(invite.token)}\n\nThank you!`)}`}>
              <Mail size={14} /> Open email draft to {invite.recipient_email}
            </a>
          )}
          <p className="text-xs text-[#84675B]">The link works without a login. It can be filled once and results appear in this dashboard instantly.</p>
          <div className="flex justify-end"><Btn onClick={onClose}>Done</Btn></div>
        </div>
      )}
    </SlidePanel>
  );
}

function ResultsPanel({ tpl, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/surveys/results/${tpl.key}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, [tpl.key]);
  return (
    <SlidePanel open onClose={onClose} title={`Results - ${tpl.name}`} subtitle={d ? `${d.completed}/${d.sent} completed · ${d.response_rate}% response rate` : ""} width="max-w-2xl" testId="survey-results-panel">
      {!d ? <p className="text-sm text-[#84675B]">Loading…</p> : d.completed === 0 ? <Empty text="No responses yet - send invites to collect feedback." /> : (
        <div className="space-y-4 pb-8">
          {d.questions.map((q) => (
            <div key={q.key} className="rounded-xl border border-[#EFEBE0] p-4" data-testid={`survey-result-${q.key}`}>
              <div className="mb-2 text-sm font-semibold text-[#3E2723]">{q.text} <span className="text-xs font-normal text-[#84675B]">({q.n} responses)</span></div>
              {q.type === "likert" && q.avg !== null && (
                <>
                  <div className="flex items-center gap-3">
                    <span className={`font-heading text-2xl font-bold ${q.avg >= 4 ? "text-[#2E7D32]" : q.avg >= 3 ? "text-[#8D6E00]" : "text-[#C62828]"}`}>{q.avg}</span>
                    <div className="h-2.5 flex-1 rounded-full bg-[#F5F2EB]"><div className="h-2.5 rounded-full bg-[#4E342E]" style={{ width: `${(q.avg / 5) * 100}%` }} /></div>
                    <span className="text-xs text-[#84675B]">/ 5</span>
                  </div>
                  <div className="mt-1.5 flex gap-2 text-[10px] text-[#7B6157]">{Object.entries(q.distribution).map(([k, v]) => <span key={k}>{k}★:{v}</span>)}</div>
                </>
              )}
              {q.type === "yesno" && q.yes_pct !== null && (
                <div className="flex items-center gap-3">
                  <span className={`font-heading text-2xl font-bold ${q.yes_pct >= 80 ? "text-[#2E7D32]" : "text-[#C62828]"}`}>{q.yes_pct}%</span>
                  <span className="text-xs text-[#7B6157]">answered Yes</span>
                </div>
              )}
              {q.type === "nps" && q.nps !== null && (
                <div className="flex items-center gap-3">
                  <span className={`font-heading text-2xl font-bold ${q.nps >= 30 ? "text-[#2E7D32]" : q.nps >= 0 ? "text-[#8D6E00]" : "text-[#C62828]"}`}>{q.nps > 0 ? "+" : ""}{q.nps}</span>
                  <span className="text-xs text-[#7B6157]">Net Promoter Score</span>
                </div>
              )}
              {q.type === "text" && (q.texts || []).length > 0 && (
                <div className="space-y-1.5">{q.texts.map((t, i) => <div key={i} className="rounded-lg bg-[#FDFCF8] border border-[#F1EEE7] p-2 text-xs text-[#3E2723]">"{t}"</div>)}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </SlidePanel>
  );
}

export default function SurveysPanel() {
  const [templates, setTemplates] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteTpl, setInviteTpl] = useState(null);
  const [resultsTpl, setResultsTpl] = useState(null);
  const load = () => api.get("/surveys/invites").then((r) => setInvites(r.data)).catch(() => {});
  useEffect(() => { api.get("/surveys/templates").then((r) => setTemplates(r.data)).catch((e) => toast.error(apiError(e))); load(); }, []);
  const fillNow = async (tpl) => {
    try {
      const r = await api.post("/surveys/invites", { survey_key: tpl.key, recipient_name: "In-clinic (kiosk)", anonymous: true });
      load();
      window.open(surveyLink(r.data.token), "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };
  const remind = async (inv) => {
    try { await api.post(`/surveys/invites/${inv.id}/remind`); toast.success(`Reminder logged for ${inv.recipient_name || "respondent"}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (inv) => {
    try { await api.delete(`/surveys/invites/${inv.id}`); toast.success("Invite removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const statFor = (key) => {
    const list = invites.filter((i) => i.survey_key === key);
    return { sent: list.length, done: list.filter((i) => i.status === "completed").length };
  };
  return (
    <div data-testid="surveys-panel">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {templates.map((t) => {
          const Icon = ICONS[t.key] || ClipboardList;
          const st = statFor(t.key);
          return (
            <Card key={t.key} className="p-5 flex flex-col" data-testid={`survey-card-${t.key}`}>
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F5F2EB] text-[#6D4C41]"><Icon size={18} /></div>
                <div>
                  <div className="font-heading text-sm font-bold text-[#3E2723]">{t.name}</div>
                  <div className="mt-0.5 text-xs text-[#7B6157]">{t.description}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-[#84675B]">{((t.sections || []).flatMap((s) => s.questions || []).length) || (t.questions || []).length} questions · {st.done}/{st.sent} completed</div>
              <div className="mt-auto pt-4 flex flex-wrap gap-2">
                <Btn className="px-3 py-1.5 text-xs" data-testid={`survey-send-${t.key}`} onClick={() => setInviteTpl(t)}><Send size={12} /> Send Invite</Btn>
                <Btn variant="outline" className="px-3 py-1.5 text-xs" data-testid={`survey-fill-${t.key}`} onClick={() => fillNow(t)}><PenLine size={12} /> Fill Now</Btn>
                <Btn variant="outline" className="px-3 py-1.5 text-xs" data-testid={`survey-results-${t.key}`} onClick={() => setResultsTpl(t)}><BarChart3 size={12} /> Results</Btn>
              </div>
            </Card>
          );
        })}
      </div>

      <h3 className="mt-8 mb-3 font-heading text-sm font-bold text-[#3E2723]">Invitations ({invites.length})</h3>
      <Card className="overflow-hidden" data-testid="survey-invites-table">
        {invites.length === 0 ? <Empty text="No invites sent yet." /> : (
          <Table headers={["Survey", "Recipient", "Sent", "Status", "Link", ""]}>
            {invites.map((inv) => (
              <tr key={inv.id} className="border-t border-[#F1EEE7] hover:bg-[#FDFCF8]" data-testid={`survey-invite-row-${inv.id}`}>
                <Td className="font-medium max-w-[190px] truncate">{inv.survey_name}</Td>
                <Td>{inv.anonymous ? <span className="italic text-[#84675B]">Anonymous</span> : inv.recipient_name || "-"}{inv.recipient_email && <div className="text-[10px] text-[#84675B]">{inv.recipient_email}</div>}</Td>
                <Td className="text-xs text-[#7B6157]">{dayjs(inv.sent_at).format("MM/DD/YY")}{inv.reminders > 0 && <div className="text-[10px]">{inv.reminders} reminder(s)</div>}</Td>
                <Td>{inv.status === "completed" ? <span className="rounded-full bg-[#E8F5E9] px-2.5 py-0.5 text-xs font-semibold text-[#2E7D32]">Completed</span> : <Badge value="pending" />}</Td>
                <Td><button data-testid={`survey-copy-${inv.id}`} onClick={() => { navigator.clipboard.writeText(surveyLink(inv.token)); toast.success("Link copied"); }} className="flex items-center gap-1 text-xs font-semibold text-[#6D4C41] hover:underline"><Copy size={12} /> Copy</button></Td>
                <Td>
                  <div className="flex gap-1">
                    {inv.status !== "completed" && <button data-testid={`survey-remind-${inv.id}`} title="Log reminder" onClick={() => remind(inv)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]"><Bell size={13} /></button>}
                    <button data-testid={`survey-delete-${inv.id}`} onClick={() => del(inv)} className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {inviteTpl && <InviteDialog tpl={inviteTpl} onClose={() => setInviteTpl(null)} onSent={load} />}
      {resultsTpl && <ResultsPanel tpl={resultsTpl} onClose={() => setResultsTpl(null)} />}
    </div>
  );
}
